import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../data/api'

const PAGE_SIZE = 10

function fmtInr(n) {
  return `₹${Number(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// Explicit timeZone — without it this renders in the viewer's own local
// time, which drifts a date near midnight IST by a day for anyone not in
// that zone (and even for IST viewers, keeps it unambiguous).
function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })
}

// IST calendar-day key (YYYY-MM-DD) for comparing an entry's date against a
// plain <input type="date"> value, which has no timezone of its own.
function istDateKey(d) {
  const ist = new Date(new Date(d).getTime() + 5.5 * 60 * 60 * 1000)
  return ist.toISOString().slice(0, 10)
}

// Every AI-usage deduction for the caller's billing account — just the
// total actually charged (the same number their balance dropped by and
// their invoice adds up), not the margin/GST math behind it. A single
// exam's generation (exam pool + trial pools) is already merged into one
// row server-side.
export default function UsageHistoryPage() {
  const navigate = useNavigate()
  const [entries, setEntries] = useState(null)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    api.get('/api/transactions/usage-history').then(setEntries).catch(() => setEntries([]))
  }, [])

  const filtered = useMemo(() => {
    if (!entries) return []
    const q = search.trim().toLowerCase()
    return entries.filter(e => {
      const key = istDateKey(e.date)
      if (fromDate && key < fromDate) return false
      if (toDate && key > toDate) return false
      if (!q) return true
      return e.label?.toLowerCase().includes(q)
        || e.actorName?.toLowerCase().includes(q)
        || e.actorEmail?.toLowerCase().includes(q)
    })
  }, [entries, fromDate, toDate, search])

  const totalAmount = useMemo(() => filtered.reduce((s, e) => s + (e.total ?? 0), 0), [filtered])
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  function applyDateFilter(next) {
    setFromDate(next.from ?? fromDate)
    setToDate(next.to ?? toDate)
    setPage(1)
  }

  function handleSearch(value) {
    setSearch(value)
    setPage(1)
  }

  return (
    <div className="px-4 sm:px-40 py-6 sm:py-12 animate-fade-up">
      <button onClick={() => navigate(-1)} className="btn-ghost mb-6 text-sm">← Back</button>

      <div className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--brand)' }}>Credits</p>
        <h2 className="text-2xl font-black tracking-tight mb-1" style={{ color: 'var(--text-1)' }}>Usage History</h2>
        <p className="text-sm" style={{ color: 'var(--text-3)' }}>Every AI usage charge deducted from your balance.</p>
      </div>

      {!entries ? (
        <p className="text-sm py-6 text-center" style={{ color: 'var(--text-3)' }}>Loading…</p>
      ) : entries.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>No AI usage yet — charges will show up here as exams get generated and graded.</p>
        </div>
      ) : (
        <>
          <div className="card p-5 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--text-3)' }}>
                {fromDate || toDate ? 'Total for selected range' : 'Total usage'}
              </p>
              <p className="text-2xl font-black" style={{ color: 'var(--text-1)' }}>{fmtInr(totalAmount)}</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <input type="text" value={search} onChange={e => handleSearch(e.target.value)}
                placeholder="Search by exam, name, or email"
                className="text-xs rounded-lg px-3 py-1.5 outline-none w-full sm:w-56"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
              <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>From</span>
              <input type="date" value={fromDate} max={toDate || undefined}
                onChange={e => applyDateFilter({ from: e.target.value })}
                className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
              <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>To</span>
              <input type="date" value={toDate} min={fromDate || undefined}
                onChange={e => applyDateFilter({ to: e.target.value })}
                className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
              {(fromDate || toDate || search) && (
                <button onClick={() => { applyDateFilter({ from: '', to: '' }); handleSearch('') }}
                  className="text-xs font-medium px-2 py-1 rounded-lg" style={{ color: 'var(--text-3)' }}>
                  ✕ Clear
                </button>
              )}
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: 'var(--text-3)' }}>No usage matches this search/date range.</p>
          ) : (
            <div className="space-y-1.5">
              {visible.map(e => (
                <div key={e._id} className="card flex items-center justify-between gap-2 flex-wrap px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{e.label}</p>
                    <p className="text-xs" style={{ color: 'var(--text-3)' }}>
                      {fmtDate(e.date)}
                      {e.actorName && <> · {e.actorName}{e.actorEmail ? ` (${e.actorEmail})` : ''}</>}
                    </p>
                  </div>
                  <p className="text-base font-bold flex-shrink-0" style={{ color: 'var(--brand)' }}>-{fmtInr(e.total)}</p>
                </div>
              ))}

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-4 flex-wrap">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
                    style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>← Prev</button>
                  <span className="text-xs" style={{ color: 'var(--text-3)' }}>Page {page} of {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
                    style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>Next →</button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
